/**
 * RemindersCenter - Central Unificada de Lembretes
 *
 * Página única em /tenant/reminders que reúne a gestão de todas as
 * réguas de lembrete do tenant. Cada módulo (Agendamento, Cobrança, ...)
 * vira uma tab dinâmica baseada no catálogo em src/lib/reminders/entityCatalog.ts.
 *
 * Reutiliza 100% o componente <ReminderRulesManager /> (zero duplicação).
 * Não substitui as páginas antigas — convivem como atalhos.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-02 v1.0.0 - Criação da Central Unificada (Fase 1)
 */

import { useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ReminderRulesManager } from '@/components/reminders/ReminderRulesManager';
import {
  getEnabledReminderEntities,
  REMINDERS_CENTER_ICON,
} from '@/lib/reminders/entityCatalog';
import { usePermissions } from '@/hooks/usePermissions';

export default function RemindersCenter() {
  const navigate = useNavigate();
  const { canView, isAdmin, loading: permsLoading } = usePermissions();

  // Lista de módulos visíveis para o usuário atual (catálogo × permissões)
  const visibleEntities = useMemo(() => {
    const all = getEnabledReminderEntities();
    if (permsLoading || isAdmin) return all;
    return all.filter((e) => !e.permissionKey || canView(e.permissionKey));
  }, [permsLoading, isAdmin, canView]);

  const [activeTab, setActiveTab] = useState<string>(
    visibleEntities[0]?.entityType ?? ''
  );

  // Estado vazio — usuário sem permissão para nenhum módulo
  if (!permsLoading && visibleEntities.length === 0) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <REMINDERS_CENTER_ICON className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <h2 className="text-base font-semibold">Sem acesso a lembretes</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Você não tem permissão para gerenciar lembretes de nenhum módulo.
              Solicite ao administrador acesso aos módulos desejados.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header da página */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <REMINDERS_CENTER_ICON className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            Central de Lembretes
          </h1>
          <p className="text-sm text-muted-foreground">
            Configure e acompanhe, em um só lugar, todas as réguas de
            lembrete automático do seu workspace.
          </p>
        </div>
      </div>

      {/* Tabs dinâmicas baseadas no catálogo */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex flex-wrap h-auto justify-start">
          {visibleEntities.map((entity) => {
            const Icon = entity.icon;
            return (
              <TabsTrigger
                key={entity.entityType}
                value={entity.entityType}
                className="gap-2"
              >
                <Icon className="h-4 w-4" />
                {entity.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {visibleEntities.map((entity) => (
          <TabsContent
            key={entity.entityType}
            value={entity.entityType}
            className="mt-4"
          >
            <Card>
              <CardContent className="p-5 space-y-4">
                {/* Atalho para a página legada (mantida em paralelo) */}
                {entity.shortcutRoute && (
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="xs"
                      className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => navigate(entity.shortcutRoute!)}
                    >
                      Abrir página dedicada
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </div>
                )}

                {/* Reuso 100% do componente genérico de regras */}
                <ReminderRulesManager
                  entityType={entity.entityType}
                  title={entity.label}
                  description={entity.description}
                  allowPositiveOffsets={entity.allowPositiveOffsets}
                  maxRules={entity.maxRules}
                  permissionKey={entity.permissionKey}
                />
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
